import axios from 'axios';
import { ImpulseElement, property, registerElement } from '@ambiki/impulse';

@registerElement('ajax-partial')
export default class AjaxPartialElement extends ImpulseElement {
  /**
   * The endpoint to fetch the partial from.
   */
  @property() src: string;

  /**
   * If true, the partial will not be loaded from the `src` until the element is visible on the screen.
   */
  @property({ type: Boolean }) lazy = false;

  /**
   * Whether the partial is being fetched or not.
   */
  @property({ type: Boolean }) loading = false;

  /**
   * Whether to replace this element with the partial (default behavior is innerHTML).
   */
  @property({ type: Boolean }) replace = false;

  observer: IntersectionObserver;
  private _detectSrcChanges = true;

  constructor() {
    super();
    this.observer = new IntersectionObserver(this.intersect.bind(this));
  }

  /**
   * Called when the element is connected to the DOM.
   */
  connected(): void {
    if (this.lazy) {
      this.observer.observe(this);
    } else {
      this.fetch();
    }
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnected(): void {
    this.clearObserver();
    this._detectSrcChanges = true;
  }

  /**
   * Called when `src` property changes.
   */
  srcChanged() {
    if (this._detectSrcChanges) {
      this.fetch();
    }
  }

  /**
   * Set the `src` property and fetch the partial.
   * @param src The endpoint to fetch the partial from.
   * @param callback A callback function to be called after the partial is fetched.
   */
  async setSrcAndFetch(src: string, callback: () => void) {
    this._detectSrcChanges = false;
    this.src = src;

    try {
      await this.fetch();
      callback();
      this._detectSrcChanges = true;
    } catch {
      this._detectSrcChanges = true;
    }
  }

  /**
   * Fetch the partial from the `src` property.
   */
  async fetch() {
    if (!this.src) return;

    this.loading = true;
    this.emit('loadstart');
    this.clearObserver();

    try {
      const { data } = await axios.get(this.src, {
        headers: {
          accept: 'text/fragment+html',
          credentials: 'same-origin',
          'X-AJAX-LOAD-ONLY': 'true',
        },
      });

      this.emit('success');
      this.insertData(data);
    } catch (error) {
      this.emit('error');
    } finally {
      this.loading = false;
      this.emit('loadend');
    }
  }

  private intersect(entries: IntersectionObserverEntry[]) {
    const lastEntry = entries.slice(-1)[0];
    if (lastEntry?.isIntersecting) {
      this.clearObserver(lastEntry.target);
      this.fetch();
    }
  }

  private insertData(data: string) {
    if (this.replace) {
      const template = document.createElement('template');
      template.innerHTML = data;
      const fragment = document.importNode(template.content, true);
      this.replaceWith(fragment);
    } else {
      this.innerHTML = data;
    }
  }

  private clearObserver(element: Element = this) {
    this.observer.unobserve(element);
  }
}
